import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/vue-query';
import { approvalRequestsService } from '@/api/approval-requests.service';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import type {
  ApprovalRequest,
  ApprovalRequestsControllerFindAllData,
} from '@carvia/ros-client-types';
import { updateItemOnQueriesData } from '@/utils/update-query-data';

export const APPROVAL_REQUESTS_QUERY_KEY = 'approval-requests';

export function useApprovalRequestsWithQuery(
  query: MaybeRefOrGetter<ApprovalRequestsControllerFindAllData['query']>,
) {
  return useInfiniteQuery({
    queryKey: [APPROVAL_REQUESTS_QUERY_KEY, query],
    queryFn: async ({ pageParam }) =>
      approvalRequestsService.getAllWithQuery({ ...toValue(query), page: pageParam }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
}

export function useApprovalRequest(id: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: [APPROVAL_REQUESTS_QUERY_KEY, id],
    queryFn: () => approvalRequestsService.getOne(toValue(id)),
    enabled: computed(() => !!toValue(id)),
  });
}

export function useApproveApprovalRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => approvalRequestsService.approve(id),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData<ApprovalRequest>([APPROVAL_REQUESTS_QUERY_KEY], result, queryClient);
    },
  });
}

export function useRejectApprovalRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rejectionReason }: { id: string; rejectionReason: string }) =>
      approvalRequestsService.reject(id, rejectionReason),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData<ApprovalRequest>([APPROVAL_REQUESTS_QUERY_KEY], result, queryClient);
    },
  });
}

export function useWithdrawApprovalRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => approvalRequestsService.withdraw(id),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData<ApprovalRequest>([APPROVAL_REQUESTS_QUERY_KEY], result, queryClient);
    },
  });
}
